import { debounce, ObjectParser } from 'app/components/data/utils'
import { DataSourceSortOrderTypes, IDataSourceConfig } from './interfaces'

/**
 * Abstract class for LocalDataSource and RemoteDataSource
 */
export abstract class DataSource<T extends object> {
  protected startIndex: number = 0
  protected size: number = 100

  protected sortBy = ''
  protected sortIndex = ''
  protected sortOrder: DataSourceSortOrderTypes = ''

  protected searchWord = ''
  protected searchPathExpressions: string[] = []

  protected listeners: Array<(records: T[], count: number) => void> = []
  protected debounceSync = debounce(() => {
    return new Promise<void>((resolve) => {
      this.getRecordsAndCount().then(([records, count]) => {
        this.updateListeners(records, count)
        resolve()
      })
    })
  }, 25)

  /**
   * keep constructor hidden to assure developers use the factory method
   * @param config
   */
  constructor(protected config: IDataSourceConfig<T>) { }

  setStartIndexAndSize(startIndex: number, size: number): this {
    this.startIndex = startIndex
    this.size = size

    return this
  }
  setSorting(sortIndex: string, sortOrder: DataSourceSortOrderTypes, sortBy?: string): this {
    this.sortIndex = sortIndex
    this.sortOrder = sortOrder
    this.sortBy = sortBy ? sortBy : sortIndex

    return this
  }

  clearSorting(): this {
    this.sortIndex = ''
    this.sortOrder = ''
    this.sortBy = ''

    return this
  }

  getSortIndex() {
    return this.sortIndex
  }

  getSortOrder() {
    return this.sortOrder
  }

  setSearch(searchWord: string, searchPathExpressions: string[]): this {
    this.searchWord = searchWord
    this.searchPathExpressions = searchPathExpressions

    return this
  }

  clearSearch(): this {
    this.searchWord = ''
    this.searchPathExpressions = []

    return this
  }

  getSearchWord() {
    return this.searchWord
  }

  getSearchIndexes() {
    return this.searchPathExpressions
  }

  onSync(listener: (records: T[], count: number) => void): () => void {
    this.listeners.push(listener)

    return () => {
      const listenerIndex = this.listeners.indexOf(listener)

      if (listenerIndex >= 0) {
        this.listeners.splice(listenerIndex, 1)
      }
    }
  }

  sync(): this {
    this.debounceSync()

    return this
  }

  abstract createRecord(record: T, urlParams?: object): Promise<T>
  abstract removeRecord(record: T, params?: object): Promise<boolean>

  protected abstract getCount(): Promise<number>
  protected abstract getRecords(): Promise<T[]>
  protected abstract getRecordsAndCount(): Promise<[T[], number]>

  protected updateListeners(records: T[], count: number) {
    this.listeners.forEach((listener) => {
      if (typeof listener === 'function') {
        listener(records, count)
      }
    })
  }

  protected getRecordsWithSearchWord(records: T[]): T[] {
    if (this.searchWord !== '') {
      records = records.filter((record) => this.hasSearchWord(record))
    }

    return records
  }

  protected searchAndSortAndFilter(records: T[]): T[] {
    records = this.getRecordsWithSearchWord(records)

    if (this.sortIndex) {
      records = records.sort((r1, r2) => this.comparator(r1, r2))

      if (this.sortOrder === 'asc') {
        records.reverse()
      }
    }

    return (records) ? records.slice(this.startIndex, this.startIndex + this.size) : records
  }
  /**
   * Does a single record have a this.searchWord in one of
   * it's indexes denoted in this.searchIndexes?
   * @param record
   */
  protected hasSearchWord(record: T): boolean {
    if (!this.searchWord) {
      return true
    }

    const searchWordLower = this.searchWord.toLowerCase()

    return this.searchPathExpressions.some((searchIndex) => {
      if (!searchIndex) {
        return false
      }
      const query = ObjectParser.QueryAndGetFirst(record, searchIndex)
      const searchValueLower = String(query).toLowerCase()

      return searchValueLower.indexOf(searchWordLower) >= 0
    })
  }

  /**
   * Standard comparator for strings and numbers within T data
   * @param d1
   * @param d2
   */
  protected comparator(d1: T, d2: T): number {
    if (!this.sortIndex) {
      throw new Error('sortIndex is not defined')
    }

    const c1 = ObjectParser.QueryAndGetFirst(d1, this.sortIndex)
    const c2 = ObjectParser.QueryAndGetFirst(d2, this.sortIndex)
    const c1IsDefined = typeof c1 !== 'undefined' && c1 !== null
    const c2IsDefined = typeof c2 !== 'undefined' && c2 !== null

    if (c1IsDefined && c2IsDefined) {
      if (c1 > c2) {
        return 1
      } else if (c2 > c1) {
        return -1
      }
    } else if (c1IsDefined) {
      return 1
    } else if (c2IsDefined) {
      return -1
    }

    return 0
  }
}



// WEBPACK FOOTER //
// ./src/app/components/data/DataSource/DataSource.ts